import React from 'react';

import cn from 'classnames';
import { omit } from 'lodash';
import PropTypes from 'prop-types';
import { BiSliderAlt } from 'react-icons/bi';

import { removeEmpty } from '../utils';
import { Button, FormInputSearch } from '../input';
import FitlerResult from '../components/FilterResult';

function FilterWrapper({
  id,
  form,
  onSubmit,
  children,
  withSearch,
  placeholder,
  inlineFilter,
  actionButton,
  defaultValue,
  filterClassName,
  withFilterResult,
  resultSubTitle,
  inlineFilterClassName,
  containerClassName,
  withFilterCount,
  contentClassName,
}) {
  const [show, setShow] = React.useState(false);
  const [filter, setFilter] = React.useState(form);
  const [animate, setAnimate] = React.useState(false);

  const handleOnClear = (e) => {
    e.preventDefault();
    setFilter(defaultValue);
  };

  const handleOnChangeInputSearch = (e) => {
    e.preventDefault();
    setFilter({
      ...filter,
      [e?.target?.name]: e?.target?.value,
    });
  };
  //
  const handleOnClearInputSearch = (e) => {
    e.preventDefault();
    setFilter({
      ...filter,
      [e.currentTarget.id]: '',
    });
    onSubmit({
      ...filter,
      [e.currentTarget.id]: '',
    });
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    document.body.removeAttribute('class');
    setAnimate(false);
    onSubmit(filter);
    setTimeout(() => {
      setShow(false);
    }, 300);
  };

  const handleOnDelete = (val) => {
    setFilter(val);
    onSubmit(val);
  };

  const handleOnResetFilter = (e) => {
    e.preventDefault();

    setFilter(defaultValue);
    onSubmit(defaultValue);
  };

  const onChange = ($v) => {
    if (typeof $v === 'function') {
      setFilter({
        ...filter,
        ...$v(),
      });
      return;
    }

    const [key] = Object.keys($v);
    const v = $v[key]();
    setFilter({
      ...filter,
      ...v,
    });
  };

  const handleOnCloseDrawer = (e) => {
    if (e) e.preventDefault();
    document.body.removeAttribute('class');
    setAnimate(false);
    setTimeout(() => {
      setShow(false);
    }, 300);
  };

  const handleOnOpenDrawer = (e) => {
    e.preventDefault();
    document.body.className = 'overflow-hidden';
    setShow(true);
    setTimeout(() => {
      setAnimate(true);
    }, 300);
  };

  const escFunction = (e) => {
    if (e.keyCode === 27) {
      handleOnCloseDrawer();
    }
  };

  const handleOnSearch = () => {
    onSubmit(filter);
  };

  document.addEventListener('keydown', escFunction, false);

  React.useEffect(() => {
    setFilter(form);
  }, [form]);

  const activeFilter = Object.keys(
    removeEmpty(omit(filter, ['page', 'per_page']))
  );

  return (
    <div className={cn('relative w-full', containerClassName)}>
      <div className="space-y-2 md:space-y-3 flex flex-col">
        <div id={id} className={cn('flex', contentClassName)}>
          <form onSubmit={handleOnSubmit} className="flex-1 w-full">
            <div className="flex flex-grow md:flex-none items-center space-x-1 md:space-x-2">
              {withSearch && (
                <div className="flex-1 md:flex-none md:w-96">
                  <FormInputSearch
                    name="keyword"
                    onSubmit={handleOnSearch}
                    onChange={handleOnChangeInputSearch}
                    placeholder={placeholder}
                    value={filter?.keyword || ''}
                    withClearButton
                    onClear={handleOnClearInputSearch}
                  />
                </div>
              )}
              {typeof inlineFilter === 'object' && (
                <div
                  className={cn({
                    [inlineFilterClassName]: inlineFilterClassName,
                    'hidden xl:block': !inlineFilterClassName,
                  })}
                >
                  {inlineFilter}
                </div>
              )}
              {typeof inlineFilter === 'function' && !children ? (
                <Button
                  onClick={handleOnOpenDrawer}
                  className="btn md outline-light text-sm relative block xl:hidden"
                  label={
                    <div className="flex md:gap-2">
                      <BiSliderAlt className="w-5 h-5" />
                      <span className="hidden md:block">Filters</span>
                      <div className="block md:hidden">
                        {activeFilter?.length ? (
                          <div className="bg-primary-500 rounded-full h-5 w-5 absolute -top-2 -right-2 flex items-center justify-center">
                            <span className="text-white text-medium text-xs">
                              {activeFilter?.length}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  }
                />
              ) : null}
              {typeof children === 'function' ? (
                <Button
                  onClick={handleOnOpenDrawer}
                  className={cn('btn md outline-light text-sm relative', {
                    [filterClassName]: filterClassName,
                  })}
                  label={
                    <div className="flex md:gap-2">
                      <BiSliderAlt className="w-5 h-5" />
                      <span className="hidden md:block">Filters</span>

                      {withFilterCount && (
                        <div className="block">
                          {activeFilter?.length ? (
                            <div className="bg-primary-500 rounded-full h-5 w-5 absolute -top-2 -right-2 flex items-center justify-center">
                              <span className="text-white text-medium text-xs">
                                {activeFilter?.length}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  }
                />
              ) : null}
            </div>
          </form>
          {actionButton && (
            <div className="md:ml-auto mb-1 ml-1 md:mb-0">{actionButton}</div>
          )}
        </div>
        {withFilterResult && (
          <FitlerResult
            filter={omit(filter, ['page', 'per_page', 'selected'])}
            onClear={handleOnResetFilter}
            onDelete={handleOnDelete}
            subTitle={resultSubTitle}
          />
        )}
      </div>
      {show && (
        <form
          onSubmit={handleOnSubmit}
          className="fixed inset-0 overflow-hidden z-40"
        >
          <div className="absolute inset-0 overflow-hidden">
            <div
              role="presentation"
              onClick={handleOnCloseDrawer}
              className={cn(
                'absolute inset-0 bg-black transition-opacity bg-opacity-25 ease-in-out',
                {
                  'opacity-100 duration-300': animate,
                  'opacity-0 duration-200': !animate,
                }
              )}
            />
            <div className="fixed inset-y-0 max-w-full right-0 flex">
              <div
                className={cn(
                  'relative w-screen transition duration-200 max-w-md',
                  {
                    'translate-x-0 ease-out': animate,
                    'translate-x-full': !animate,
                  }
                )}
              >
                <div className="flex flex-col bg-white w-full h-full py-5">
                  <div className="flex items-center px-5">
                    <p className="text-xl text-black font-bold">
                      Advanced Filter
                    </p>
                    <div className="ml-auto">
                      <button
                        type="button"
                        tabIndex="-1"
                        className="rounded-full transition duration-300 ease-in-out group outline-none group hover:bg-primary-500 p-1"
                        onClick={handleOnCloseDrawer}
                      >
                        <svg
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          className="w-6 h-6 transition duration-300 ease-in-out group-hover:text-white text-primary-500 rounded-full"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="4"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 overflow-y-auto px-5 space-y-3 pointer-events-auto">
                    <div className="flex-1 space-y-3">
                      {typeof children === 'function' &&
                        children(filter, onChange)}
                    </div>
                    <div className="flex gap-3">
                      <Button
                        className="btn md outline-light w-full"
                        label="Clear"
                        onClick={handleOnClear}
                      />
                      <Button
                        type="submit"
                        label="Apply Filter"
                        className="btn md primary w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

FilterWrapper.defaultProps = {
  form: {},
  id: 'filter',
  actionButton: false,
  placeholder: 'Search...',
  defaultValue: {},
  withFilterResult: true,
  children: null,
  inlineFilter: false,
  filterClassName: false,
  withSearch: true,
  resultSubTitle: false,
  inlineFilterClassName: false,
  containerClassName: false,
  withFilterCount: false,
  contentClassName: '',
};

FilterWrapper.propTypes = {
  id: PropTypes.string,
  withSearch: PropTypes.bool,
  withFilterCount: PropTypes.bool,
  placeholder: PropTypes.string,
  withFilterResult: PropTypes.bool,
  contentClassName: PropTypes.string,
  form: PropTypes.instanceOf(Object),
  inlineFilterClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  containerClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  defaultValue: PropTypes.instanceOf(Object),
  inlineFilter: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
    PropTypes.element,
  ]),
  actionButton: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.instanceOf(Object),
  ]),
  resultSubTitle: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
  children: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  filterClassName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onSubmit: PropTypes.instanceOf(Function).isRequired,
};

export default React.memo(FilterWrapper);
